import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import api from '../../api/client';
import { Button } from '../common';
import { formatDate } from '../../utils/formatters';

/**
 * EditTopicModal - Edit topic details and status
 */
const EditTopicModal = ({ topic, onClose, onSave }) => {
  const [editedTopic, setEditedTopic] = useState({
    title: topic?.title || '',
    details: topic?.description || topic?.details || '',
    closed: topic?.closed || topic?.closedAt != null,
    closedAt: topic?.closedAt || null,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Update state when topic prop changes
  useEffect(() => {
    if (topic) {
      console.log('EditTopicModal - Loading topic:', topic);
      setEditedTopic({
        title: topic.title || '',
        details: topic.description || topic.details || '',
        closed: topic.closed || topic.closedAt != null,
        closedAt: topic.closedAt || null,
      });
    }
  }, [topic]);

  const handleSave = async () => {
    if (!editedTopic.title.trim()) {
      setError('Topic title is required');
      return;
    }
    setError('');
    setSaving(true);
    try {
      await api.patch(`/topics/${topic.id}`, {
        title: editedTopic.title,
        details: editedTopic.details,
        closed: editedTopic.closed,
        closedAt: editedTopic.closed ? (editedTopic.closedAt || new Date().toISOString()) : null,
      });
      onSave();
    } catch (error) {
      console.error('Failed to update topic:', error);
      setError('Failed to update topic');
    } finally {
      setSaving(false);
    }
  };

  if (!topic) return null;

  return (
    <div className="modal-overlay">
      <div className="modal modal-large">
        <div className="modal-header">
          <h2 className="modal-title">Edit Topic</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          <div className="form-group">
            <label className="form-label">Title *</label>
            <input
              type="text"
              className="form-input"
              value={editedTopic.title}
              onChange={(e) => setEditedTopic({ ...editedTopic, title: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Details (optional)</label>
            <textarea
              className="form-input"
              value={editedTopic.details}
              onChange={(e) => setEditedTopic({ ...editedTopic, details: e.target.value })}
              rows={4}
            />
          </div>
          <div className="form-group">
            <label className="form-label">
              <input
                type="checkbox"
                checked={editedTopic.closed}
                onChange={(e) => setEditedTopic({
                  ...editedTopic,
                  closed: e.target.checked,
                  closedAt: e.target.checked ? (editedTopic.closedAt || new Date().toISOString()) : null
                })}
                style={{ marginRight: '0.5rem' }}
              />
              Mark as Closed
            </label>
            {editedTopic.closed && editedTopic.closedAt && (
              <p style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: 'var(--color-text-muted)' }}>
                Closed: {formatDate(editedTopic.closedAt)}
              </p>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
};

EditTopicModal.propTypes = {
  topic: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default EditTopicModal;
